//
// 03/05/2019 note:
// When changing any site settings, be sure to follow the process in the "Settings Support Work In-take Process" wiki page:
//    https://mytableau.tableaucorp.com/x/coDWD
//

import _ from 'underscore';

import { getAppStore } from '../../app/AppStore';
import { NavLocationSelectors } from '../../app/selectors/NavigationStateSelectors';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { Location } from '../../common/react/Location';
import { SiteSettingServerAdminTelemetryEvent } from '../../common/telemetryEvents/TelemetryEvents';
import { buildSiteSettingServerAdminTelemetryEvent, getSiteSettingUUID } from '../../common/telemetryEvents/util/SiteSettingTelemetryUtil';
import { NumberInputRange } from '../../ts/directives/tbRangeInputValidator';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ByteSizeConversionFactor } from '../../commonInfra/utils/SizeFilter';
import { L10n } from '../../ts/L10n/L10n';
import { Module } from '../../ts/Module';
import { Sites } from '../../ts/resources/Sites';
import { GetNumExcessRevisionsRequest, IGetNumExcessRevisionsParams, IGetNumExcessRevisionsResult } from '../../ts/ServerApi/requests/GetNumExcessRevisionsRequest';
import { GetNumManagedKeychainsRequest, IGetNumManagedKeychainParams, IGetNumManagedKeychainResult } from '../../ts/ServerApi/requests/GetNumManagedKeychainRequest';
import { IError } from '../../httpClient/serverApi/types/IError';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ClearOrphanedManagedKeychainAction } from '../../ts/services/ClearOrphanedManagedKeychainAction';
import { ClearSavedRevisionsAction } from '../services/ClearSavedRevisionsAction';
import { ILicensingInfoWithTotals, LicensesService } from '../../ts/services/LicensesService';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { SiteSwitchService } from '../../ts/services/SiteSwitchService';
import { ISettingsCoordinatorCtrlScope, SettingsFormService } from '../directives/tbSettingsCoordinator';
import { calculateUserMaxCounts } from '../general-settings/components/sections/manage-users-section/ManageUsersAdminsOption';
import { ISiteSettingsForServerAdmin } from '../types/ISite';
import { ISiteSettingsForSiteAdmin, UserQuotaMode } from '../types/ISiteSettingsForSiteAdmin';
import { HelpService } from '../../commonInfra/services/HelpService';
import { Telemetry } from '../../commonInfra/services/Telemetry';
import { ServerSettingsUnauthenticated } from '../services/ServerSettingsUnauthenticated';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { SiteAvailabilityState } from '../../sites/SitePropertyTypes';
import { SiteIdValidRegExp } from '../general-settings/components/sections/site-name-and-id/SiteNameAndIdBusinessLogic';

export interface ISiteSettingsForServerAdminFormScope extends ISettingsCoordinatorCtrlScope {

  siteSettingsForServerAdminForm: ng.IFormController; // from siteSettingsForServerAdminForm.pug

  hideAvailability: boolean;
  hideRevisionHistoryLimitWarning: boolean;
  autoSelectSiteName: boolean;
  active: string;
  suspended: string;
  url: string;
  defaultSubscriptionsEmail: string;
  userCountMax: number;
  tierCreatorCountMax: number;
  tierExplorerCountMax: number;
  tierViewerCountMax: number;
  storageQuotaMax: number;
  versionLimitMin: number;
  versionLimitMax: number;
  isSeatLicensing: boolean;
  isSubscriptionLicensing: boolean;
  unbindNameWatcher: Function;
  clearOrphanedManagedKeychain: () => void;
  clearSavedRevisions: () => void;
  excessRevisionsMessage: string;
  isGuestEnabledOnServer: () => boolean;
  isSessionMultiSite: () => boolean;
  cacheWarmupLearnMoreHref: string;
  showSiteLevelGuestAccessControl: () => boolean;
  isDefaultSite: boolean;
  flowsOnServerEnabled: boolean;
  schedulingFlowsEnabled: boolean;
  catalogingOnServerEnabled: boolean;
  catalogingLearnMoreHref: string;
  managedKeychainCountMessage: string;
  calculateManagedKeychainCount: () => void;
  extractRefreshAutoSuspendLearnMoreHref: string;
}

export class SiteSettingsForServerAdminFormCtrl {

  private master: ISiteSettingsForServerAdmin;
  private server: SessionManagementService;

  public static $inject = [
    '$scope',
    '$location',
    '$q',
    '$translate',
    'SettingsFormService',
    'ClearOrphanedManagedKeychainAction',
    'ClearSavedRevisionsAction',
    'Sites',
    'LicensesService',
  ];

  public static InputDebounceMs: number = 200;

  constructor(
    private scope: ISiteSettingsForServerAdminFormScope,
    private $location: ng.ILocationService,
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
            formService: SettingsFormService,
    private clearOrphanedManagedKeychainAction: ClearOrphanedManagedKeychainAction,
    private clearSavedRevisionsAction: ClearSavedRevisionsAction,
    private sitesResource: Sites,
            licenses: LicensesService,
  ) {
    //region Migrated to corresponding settings and site settings for server admin page
    this.server = getSessionManagementServiceInstance();
    if (this.scope.isDefaultSite == null) {
      this.scope.isDefaultSite = this.scope.siteSettingsForServerAdmin.settings
        && this.scope.siteSettingsForServerAdmin.settings.urlName === '';
    }

    this.scope.active = SiteAvailabilityState.ACTIVE;
    this.scope.suspended = SiteAvailabilityState.SUSPENDED;
    this.scope.defaultSubscriptionsEmail = this.server.defaultSubscriptionsEmail();
    if(! FeatureFlags.settingsReactMigration) {
      licenses.fetch().then((licensingInfo: ILicensingInfoWithTotals) => {
        if (licensingInfo) {
          Object.assign(this.scope, calculateUserMaxCounts(licensingInfo));
        }
      });
    }
    this.scope.storageQuotaMax = NumberInputRange.LONG.MAX / ByteSizeConversionFactor.ONE_GB;
    this.scope.versionLimitMin = 2;
    this.scope.versionLimitMax = 10000;
    this.scope.server = this.server;

    this.scope.isGuestEnabledOnServer = () => ServerSettingsUnauthenticated.isGuestEnabled();
    this.scope.isSessionMultiSite= () => this.server.isSessionMultiSite();
    this.scope.showSiteLevelGuestAccessControl= () => (!this.scope.isDefaultSite && !this.scope.isSessionMultiSite()) || this.scope.isSessionMultiSite();
    this.scope.cacheWarmupLearnMoreHref = HelpService.getServerHelpUrlByCategory('workbookrefresh');
    this.scope.catalogingLearnMoreHref = HelpService.getServerHelpUrlByCategory('catalog-site-setting');
    this.scope.extractRefreshAutoSuspendLearnMoreHref = HelpService.getServerHelpUrlByCategory('extract-auto-suspend');
    //endregion

    this.initializeUnsetValues();

    //region Handled by Formik
    this.master = angular.copy(this.scope.siteSettingsForServerAdmin);

    this.scope.sharedForm = formService;
    this.scope.localForm = {
        formController: this.scope.siteSettingsForServerAdminForm,
        save: () => this.callServerToUpdateSiteSettings(),
        cancel: () => this.revertChanges(),
        isBusy: () => this.scope.localForm.busy,
        isFormUnchanged: () => angular.equals(this.master, this.scope.siteSettingsForServerAdmin),
        resetSettings: null
    };
    //endregion

    //region Handled by ReactSiteSettingsForServerAdminSubFormik.tsx
    // Ported over to SiteNameAndIdBusinessLogic
    this.scope.$watch('siteSettingsForServerAdmin.settings.urlName', (value: string) => {
      scope.url = this.$location.absUrl().replace(this.$location.url(), '') +
        SiteSwitchService.getSitePathFragment(value);
    });

    this.scope.$watch('siteSettingsForServerAdmin.settings', (newValue: any) => {
      this.scope.$broadcast('settings.change', newValue);
    }, true);

    // Availablity setting has been ported
    this.scope.$watch('siteSettingsForServerAdmin.availability', (newValue: string) => {
      this.scope.$broadcast('availability.change', newValue);
    });
    //endregion

    !FeatureFlags.settingsReactMigration && this.scope.$watch('siteSettingsForServerAdmin.settings.managedKeychainCountEnabled', () => {
       this.calculateManagedKeychainCount();
    });

    this.scope.clearOrphanedManagedKeychain = () => this.clearOrphanedManagedKeychain();

    //region Migrated to RevisionHistorySection.tsx
    this.scope.clearSavedRevisions = () => this.clearSavedRevisions(this.scope.siteId);

    if (!this.scope.hideRevisionHistoryLimitWarning && !FeatureFlags.settingsReactMigration) {
      // Async call to get the data for the Revision History Limit warning message, e.g. "Setting the limit to this will delete X revisions"
      const debouncedUpdate = _.debounce((newValue: number) => {
        this.scope.$evalAsync(() => {
          this.setExcessRevisionsMessage(newValue);
        });
      }, SiteSettingsForServerAdminFormCtrl.InputDebounceMs);

      // Kick off debounced update when contentVersionLimit changes
      this.scope.$watch('siteSettingsForServerAdmin.settings.contentVersionLimit', (value: number) => {
        this.scope.excessRevisionsMessage = '';
        if (_.isNumber(value)) {
          debouncedUpdate(value);
        }
      });
    }
    //endregion
  }

  //region Not needed
  private initializeUnsetValues(){
        // Initialize unset values so angular doesn't overwrite them with empty string
    if (this.scope.siteSettingsForServerAdmin && this.scope.siteSettingsForServerAdmin.settings) {
      if (this.scope.siteSettingsForServerAdmin.settings.userQuotaMode === UserQuotaMode.SERVER_LIMIT) {
        this.scope.siteSettingsForServerAdmin.settings.userQuota = null;
        this.scope.siteSettingsForServerAdmin.settings.tierCreatorCapacity = null;
        this.scope.siteSettingsForServerAdmin.settings.tierExplorerCapacity = null;
        this.scope.siteSettingsForServerAdmin.settings.tierViewerCapacity = null;
      }
      if (this.scope.siteSettingsForServerAdmin.settings.userQuotaMode === UserQuotaMode.SITE_TIERED_CAPACITY) {
        this.scope.siteSettingsForServerAdmin.settings.userQuota = null;
      }
      if (this.scope.siteSettingsForServerAdmin.settings.userQuotaMode === UserQuotaMode.SITE_USER_QUOTA) {
        this.scope.siteSettingsForServerAdmin.settings.tierCreatorCapacity = null;
        this.scope.siteSettingsForServerAdmin.settings.tierExplorerCapacity = null;
        this.scope.siteSettingsForServerAdmin.settings.tierViewerCapacity = null;
      }
      if (!this.scope.siteSettingsForServerAdmin.settings.storageQuotaEnabled && !FeatureFlags.settingsReactMigration) {
        this.scope.siteSettingsForServerAdmin.settings.storageQuotaBytes = null;
      }
      if (!this.scope.siteSettingsForServerAdmin.settings.customSubscriptionEmailEnabled) {
        this.scope.siteSettingsForServerAdmin.settings.customSubscriptionEmail = angular.copy(this.scope.defaultSubscriptionsEmail);
      }
      if (!(this.scope.siteSettingsForServerAdmin.settings.contentVersionLimit >= 0)) {
        this.scope.siteSettingsForServerAdmin.settings.contentVersionLimit = null;
      }
      // Copy a scrubbed version of the inputted name into the ID field until the ID is edited
      if (this.scope.siteSettingsForServerAdmin.settings.urlName === '' && !this.scope.isDefaultSite) {
        this.scope.unbindNameWatcher = this.scope.$watch('siteSettingsForServerAdmin.settings.name', (value: string) => {
          this.scope.siteSettingsForServerAdmin.settings.urlName = _.isString(value) ? value.replace(SiteIdValidRegExp.InvalidFind, '') : '';
        });
      }
    }
    this.scope.flowsOnServerEnabled = this.server.flowsOnServerEnabled();
    this.scope.catalogingOnServerEnabled = this.server.catalogingOnServerEnabled();
  }
  //endregion

  //region Handled by Formik
  private revertChanges(): void {
    this.scope.siteSettingsForServerAdmin = angular.copy(this.master);
    // TODO: Use Angular 1.3 $validators/$validate and pull validation out of $parsers/$formatters
    _.each(this.scope.siteSettingsForServerAdminForm.$error, (error: ng.INgModelController[], key: string) => {
      if (error) {
        error[0].$setValidity(key, true);
      }
    });
  }
  //endregion

  // Used in edit site modal
  public save(): void {
    this.callServerToUpdateSiteSettings();
  }

  //region Migrated to ManageUsersSection.tsx
  public cleanupUserQuotaValues(): void {
    const mode = this.scope.siteSettingsForServerAdmin.settings.userQuotaMode;
    if (mode === UserQuotaMode.SERVER_LIMIT) {
      this.scope.siteSettingsForServerAdmin.settings = {
        ...this.scope.siteSettingsForServerAdmin.settings,
        tierCreatorCapacity: null,
        tierExplorerCapacity: null,
        tierViewerCapacity: null,
        userQuota: null,
      };
    }
    if (mode === UserQuotaMode.SITE_USER_QUOTA) {
      this.scope.siteSettingsForServerAdmin.settings = {
        ...this.scope.siteSettingsForServerAdmin.settings,
        tierCreatorCapacity: null,
        tierExplorerCapacity: null,
        tierViewerCapacity: null,
      };
    }
    if (mode === UserQuotaMode.SITE_TIERED_CAPACITY) {
      this.scope.siteSettingsForServerAdmin.settings = {
        ...this.scope.siteSettingsForServerAdmin.settings,
        userQuota: null,
      };
    }
  }
  //endregion

  private callServerToUpdateSiteSettings(): ng.IPromise<any> {
    const updatePromises: ng.IPromise<any>[] = [];
    this.cleanupUserQuotaValues();
    const site = angular.copy(this.scope.siteSettingsForServerAdmin);

    this.scope.localForm.busy,
    updatePromises.push(this.sitesResource.updateSiteSettingsForServerAdmin(
      site.id,
      site.settings
    ));

    // Availability should now be a subform with it's own API call
    if (site.availability !== this.master.availability) {
      if (site.availability === SiteAvailabilityState.ACTIVE) {
        updatePromises.push(this.sitesResource.activate([site.id]));
      } else {
        updatePromises.push(this.sitesResource.suspend([site.id]));
      }
    }

    // todo use ResultHelper.combineResults here instead of duplicating the logic
    return this.$q.all(updatePromises)
      .then((results: IResult[]) => {
        let errors: IError[] = [];

        angular.forEach(results, (result: IResult) => {
          if (result.errors && result.errors.length) {
            errors = errors.concat(result.errors);
          }
        });

        if (errors.length) {
          this.scope.sharedForm.state.updateFailure(errors, site);
          return this.$q.reject();
        } else {
          this.updateSuccess(site);
        }
      })
      .finally(() => this.scope.localForm.busy = false);
  }

  private clearOrphanedManagedKeychain(){
    this.clearOrphanedManagedKeychainAction.confirmDeleteOrphanedManagedKeychain();
    this.scope.siteSettingsForServerAdmin.settings.managedKeychainCountEnabled = false;
  }

  //region Migrated to RevisionHistoryClearButton.tsx
  private clearSavedRevisions(siteId: string) {
    this.clearSavedRevisionsAction.confirmDeleteSiteRevisions(siteId);
  }
  //endregion

  // Success/Error Handlers

  private updateSuccess(site: ISiteSettingsForServerAdmin): void {
    // Only need to update current view and state if we're in server or site settings
    if (!NavLocationSelectors.inSettingsLocation(getAppStore().getState())) {
      return;
    }

    // send site setting for server admin telemetry events
    Telemetry.sendEvent<SiteSettingServerAdminTelemetryEvent>(buildSiteSettingServerAdminTelemetryEvent(site, this.master,  getSiteSettingUUID(false)));

    // Trigger update of session info state as settings update may have changed it.
    this.server.refreshSessionInfo();
    // Refresh session info when changing a setting that affects it
    if (site.settings.name !== this.master.settings.name ||
        site.settings.catalogingOnSiteEnabled !== this.master.settings.catalogingOnSiteEnabled ||
        site.settings.schedulingFlowsEnabled !== this.master.settings.schedulingFlowsEnabled ||
        site.settings.authoringEnabled !== this.master.settings.authoringEnabled ||
        site.settings.webEditingEnabled !== this.master.settings.webEditingEnabled) {
      this.server.refreshSessionInfo(); // Update site picker and AuthenticationService
    }

    // Changed site url name in site settings page
    if (site.settings.urlName !== '' && site.settings.urlName !== this.master.settings.urlName) {
      const urls = new SpacesSiteUrls({siteUrlName: site.settings.urlName});
      Location.instance.setUrl(urls.settingsUrl());
    }

    this.master = angular.copy(site);
  }

  // TODO: Copy this to the React Setting
  //region Not a real setting. This is a remnant of a previous implementation of the managed keychain setting
  // which added a new setting to the scope to selectively show the counts when the checkbox is enabled,
  // but to do that, ISite needed to be modified and that made it look like the API has that field. It doesn't
  private calculateManagedKeychainCount(): void {
    if (!this.scope.siteSettingsForServerAdmin.settings.managedKeychainCountEnabled){
      return;
    }
    this.scope.managedKeychainCountMessage = '';
    const debouncedUpdate = _.debounce(() => {
      this.scope.$evalAsync(() => {
        this.setManagedKeychainCountMessage();
      });
    }, SiteSettingsForServerAdminFormCtrl.InputDebounceMs);
    debouncedUpdate();
  }
  //endregion

  //region Migrated to ManagedKeychainCleanup.tsx
  private setManagedKeychainCountMessage(): void {
    WebHttpClient.sendRequest(new GetNumManagedKeychainsRequest({}))
      .then((result: IGetNumManagedKeychainResult) => {
        const orphanedManagedKeychainCount = result.orphanedManagedKeychainCount;
        const totalManagedKeychainCount = result.totalManagedKeychainCount;
        this.scope.managedKeychainCountMessage = this.$translate.instant('managedKeychainCountValuesMsg',
        {
          TOTAL_COUNT: L10n.localizedCount(totalManagedKeychainCount),
          ORPHANED_COUNT: L10n.localizedCount(orphanedManagedKeychainCount)});
        }
      );
  }
  //endregion

  //region Migrated to RevisionInputBusinessLogic.ts
  private setExcessRevisionsMessage(input: number): void {
    const params: IGetNumExcessRevisionsParams = {
      limit: input
    };

    // This code contains a bug, because the getNumExcessRevisions request is implicitly site-scoped,
    // but this UI can show up in the edit site settings dialog from the all sites list.
    // The effect is that we'll fetch the excess revisions info for whatever site the
    // server admin is signed into regardless of which site they're editing.
    // TFS: 815654
    WebHttpClient.sendRequest(new GetNumExcessRevisionsRequest(params))
      .then((result: IGetNumExcessRevisionsResult) => {
        if (input === this.scope.siteSettingsForServerAdmin.settings.contentVersionLimit) {
          const workbookRevisionCount = result.workbookRevisionCount;
          const datasourceRevisionCount = result.datasourceRevisionCount;
          const flowRevisionCount = result.flowRevisionCount;

          if (this.master.settings.flowsOnSiteEnabled &&
             (flowRevisionCount > 0 || workbookRevisionCount > 0 || datasourceRevisionCount > 0)) {
            this.scope.excessRevisionsMessage = this.$translate.instant('contentVersionLimitWarningWithWorkbookDataSourceAndFlowValues',
              { LIMIT_COUNT: L10n.localizedCount(input),
                EXCESS_WORKBOOK_COUNT: L10n.localizedCount(workbookRevisionCount),
                EXCESS_DATASOURCE_COUNT: L10n.localizedCount(datasourceRevisionCount),
                EXCESS_FLOW_COUNT: L10n.localizedCount(flowRevisionCount)});
          } else if (workbookRevisionCount > 0 || datasourceRevisionCount > 0) {
            this.scope.excessRevisionsMessage = this.$translate.instant('contentVersionLimitWarningWithWorkbookAndDataSourceValues',
              { LIMIT_COUNT: L10n.localizedCount(input),
                EXCESS_WORKBOOK_COUNT: L10n.localizedCount(workbookRevisionCount),
                EXCESS_DATASOURCE_COUNT: L10n.localizedCount(datasourceRevisionCount)});
          }
        }
      }
    );
  }
  //endregion

}

Module.instance().controller('SiteSettingsForServerAdminFormCtrl', SiteSettingsForServerAdminFormCtrl);
